import { Controller } from '@hotwired/stimulus';

export default class extends Controller {
  static targets = [
    'bundle',
    'dayStrategy',
    'strategy',
    'dashboard',
    'unitCheckbox',
    'outcomeCheckbox',
    'ericStrategyCheckbox'
  ];

  connect() {
    this.fixOverlappingDateLabels();
    this.unitsSelected = new Set();
    this.showProspective = true;
    this.showNotProspective = true;
    this.showModified = true;
    this.showNotModified = true;
    this.outcomesSelected = new Set();
    this.outcomeCount = this.outcomeCheckboxTargets.length;
    this.ericStrategiesSelected = new Set();
    this.ericStrategiesCount = this.ericStrategyCheckboxTargets.length;
    this.strategyTargets.forEach((strategyTarget) => {
      this.unitCheckboxTargets.forEach((checkbox) => {
        const unitId = Number.parseInt(
          checkbox.dataset['lists-DashboardUnitIdParam']
        );
        this.unitsSelected.add(unitId);
      });
      this.outcomeCheckboxTargets.forEach((checkbox) => {
        const outcomeId = Number.parseInt(
          checkbox.dataset['lists-DashboardOutcomeIdParam']
        );
        this.outcomesSelected.add(outcomeId);
      });
      this.strategyTargetConnected(strategyTarget);
      this.ericStrategyCheckboxTargets.forEach((checkbox) => {
        const ericId = Number.parseInt(
          checkbox.dataset['lists-DashboardEricStrategyIdParam']
        );
        this.ericStrategiesSelected.add(ericId);
      });
    });
    this.bundleTargets.forEach(this.bundleTargetConnected);
    this.prospective = { showTrue: true, showFalse: true };
  }

  dashboardTargetConnected() {
    this.dashboardTarget.style.height = `calc((${this.strategyTargets.length} * 40px) + (${this.bundleTargets.length} * 36px) + 315px)`;
  }

  strategyTargetConnected(element) {
    // position and size strategy
    const dayWidth = element
      .closest('.day-strategy')
      .getBoundingClientRect().width;
    element.style.left = `${element.dataset.dayOffset * dayWidth}px`;
    element.style.width = `${(element.dataset.dayCount / element.dataset.daysSlicedBy) * dayWidth}px`;
    // position strategy label
    const label = element.querySelector('.strategy-label');
    if (label) {
      const labelRect = label.getBoundingClientRect();
      if (labelRect.x < 0) {
        label.style['margin-left'] = '-50px';
      } else {
        const bodyWidth = document
          .querySelector('body')
          .getBoundingClientRect().width;
        if (labelRect.x + labelRect.width > bodyWidth) {
          label.style['margin-right'] = '-20px';
        }
      }
    }
  }

  bundleTargetConnected(element) {
    const dayWidth = element
      .closest('.day-strategy')
      .getBoundingClientRect().width;
    element.style.width = `${(element.dataset.dayCount / element.dataset.daysSlicedBy) * dayWidth}px`;
  }

  /* private */

  fixOverlappingDateLabels() {
    const singleOffsetInPixels = 20;
    const labels = document.querySelectorAll('.x-label');
    var cumulativeOffset = singleOffsetInPixels;
    for (let n = 1; n < labels.length; n++) {
      let e1 = labels[n - 1];
      let e2 = labels[n];

      if (this.checkOverlap(e1, e2)) {
        e2.style.top = `${e2.offsetTop + cumulativeOffset}px`;
        cumulativeOffset += singleOffsetInPixels;
      }
    }
  }

  checkOverlap(leftElement, rightElement) {
    const leftParentRectangle =
      leftElement.parentElement.getBoundingClientRect();
    const rightParentRectangle =
      rightElement.parentElement.getBoundingClientRect();

    return (
      rightParentRectangle.x <
      leftParentRectangle.x + leftElement.offsetHeight * Math.sqrt(2)
    );
  }

  toggleUnit(event) {
    if (event.target.checked) {
      this.unitsSelected.add(event.params.unitId);
    } else {
      this.unitsSelected.delete(event.params.unitId);
    }

    this.applyAllFilters();
  }

  toggleProspective(event) {
    this.showProspective = event.target.checked;

    this.applyAllFilters();
  }

  toggleNotProspective(event) {
    this.showNotProspective = event.target.checked;

    this.applyAllFilters();
  }

  toggleOutcome(event) {
    if (event.target.checked) {
      this.outcomesSelected.add(event.params.outcomeId);
    } else {
      this.outcomesSelected.delete(event.params.outcomeId);
    }

    this.applyAllFilters();
  }

  toggleEricStrategy(event) {
    if (event.target.checked) {
      this.ericStrategiesSelected.add(event.params.ericStrategyId);
    } else {
      this.ericStrategiesSelected.delete(event.params.ericStrategyId);
    }

    this.applyAllFilters();
  }

  toggleModified(event) {
    this.showModified = event.target.checked;

    this.applyAllFilters();
  }

  toggleNotModified(event) {
    this.showNotModified = event.target.checked;

    this.applyAllFilters();
  }

  applyAllFilters() {
    this.dayStrategyTargets.forEach((dayStrategy) => {
      dayStrategy.classList.add('not-highlighted');
      dayStrategy.querySelector('.strategy-label').classList.add('grayed-out');
    });
    this.dayStrategyTargets.forEach((dayStrategy) => {
      this.showSelectedUnitStrategies(dayStrategy);
      this.showProspectiveStrategies(dayStrategy);
      this.showNotProspectiveStrategies(dayStrategy);
      this.showSelectedOutcomeStrategies(dayStrategy);
      this.showSelectedEricStrategies(dayStrategy);
      this.showModifiedStrategies(dayStrategy);
      this.showNotModifiedStrategies(dayStrategy);
    });
  }

  showSelectedUnitStrategies(dayStrategy) {
    if (
      this.unitsSelected.intersection(
        new Set(JSON.parse(dayStrategy.dataset.unitIds))
      ).size > 0
    ) {
      dayStrategy.classList.remove('not-highlighted');
      dayStrategy
        .querySelector('.strategy-label')
        .classList.remove('grayed-out');
    }
  }

  showProspectiveStrategies(dayStrategy) {
    if (this.showProspective && dayStrategy.dataset.wasProspective === 'true') {
      dayStrategy.classList.remove('not-highlighted');
      dayStrategy
        .querySelector('.strategy-label')
        .classList.remove('grayed-out');
    }
  }

  showNotProspectiveStrategies(dayStrategy) {
    if (
      this.showNotProspective &&
      dayStrategy.dataset.wasProspective === 'false'
    ) {
      dayStrategy.classList.remove('not-highlighted');
      dayStrategy
        .querySelector('.strategy-label')
        .classList.remove('grayed-out');
    }
  }

  showSelectedOutcomeStrategies(dayStrategy) {
    if (this.outcomeCount === 0) return;

    if (
      (dayStrategy.dataset.outcomeIds != null &&
        this.outcomesSelected.intersection(
          new Set(JSON.parse(dayStrategy.dataset.outcomeIds))
        ).size > 0) ||
      this.outcomesSelected.size === this.outcomeCount
    ) {
      dayStrategy.classList.remove('not-highlighted');
      dayStrategy
        .querySelector('.strategy-label')
        .classList.remove('grayed-out');
    }
  }

  showSelectedEricStrategies(dayStrategy) {
    if (this.ericStrategiesCount === 0) return;

    if (
      (dayStrategy.dataset.ericStrategyId != null &&
        this.ericStrategiesSelected.has(
          Number.parseInt(dayStrategy.dataset.ericStrategyId)
        )) ||
      this.outcomesSelected.size === this.outcomeCount
    ) {
      dayStrategy.classList.remove('not-highlighted');
      dayStrategy
        .querySelector('.strategy-label')
        .classList.remove('grayed-out');
    }
  }

  showModifiedStrategies(dayStrategy) {
    if (this.showModified && dayStrategy.dataset.modified === 'true') {
      dayStrategy.classList.remove('not-highlighted');
      dayStrategy
        .querySelector('.strategy-label')
        .classList.remove('grayed-out');
    }
  }

  showNotModifiedStrategies(dayStrategy) {
    if (this.showNotModified && dayStrategy.dataset.modified === 'false') {
      dayStrategy.classList.remove('not-highlighted');
      dayStrategy
        .querySelector('.strategy-label')
        .classList.remove('grayed-out');
    }
  }
}
